/**
 * Featured collections product carousel.
 *
 * Loop mode works by padding the track with clones instead of transforming it:
 * the original cards stay untouched and get a strip of copies on each side, wide
 * enough to cover the viewport plus a couple of extra cards. Scrolling past
 * either end therefore always shows plausible content, and the scroll position
 * is silently rewound by exactly one set width once the user comes to rest.
 * Because the clone under the viewport is pixel-identical to the original it
 * maps onto, that rewind is invisible.
 *
 * All measurements are signed scroll offsets relative to the first cell's
 * resting position, so the arithmetic also holds in right-to-left locales where
 * `scrollLeft` runs negative, and it accounts for the scroll padding that the
 * `bleed` utility adds on small screens.
 */
(() => {
  const CLONE_ATTRIBUTE = 'data-fc-clone';

  /** Attributes that may point at an id we rename while cloning. */
  const ID_REFERENCE_ATTRIBUTES = [
    'for',
    'form',
    'list',
    'aria-controls',
    'aria-labelledby',
    'aria-describedby',
    'aria-owns',
  ];

  /** Extra card slots cloned on each side, on top of what the viewport needs. */
  const EDGE_BUFFER = 2;

  /**
   * Snippets pull in their own stylesheet, so the track holds a `<link>` before
   * every card. Those elements generate no box and would poison every
   * measurement, so geometry and cloning only ever consider real cells.
   */
  const NON_RENDERED_TAGS = new Set(['LINK', 'SCRIPT', 'STYLE', 'TEMPLATE', 'NOSCRIPT', 'META']);

  let instanceCount = 0;

  class FcLoopCarousel extends HTMLElement {
    connectedCallback() {
      this._track   = this.querySelector('[data-track]');
      this._prevBtn = this.querySelector('[data-prev]');
      this._nextBtn = this.querySelector('[data-next]');
      if (!this._track) return;

      this._uid = ++instanceCount;
      this._originals = this._cells().filter((cell) => !cell.hasAttribute(CLONE_ATTRIBUTE));

      this._anchor    = null; // cell every scroll offset is measured against
      this._looping   = false;
      this._origin    = 0; // scroll offset of the first original card
      this._span      = 0; // scroll distance covered by one full set of cards
      this._slot      = 0; // scroll distance covered by one card (card + gap)
      this._scrolling = false;
      this._dragging  = false;
      this._wrapFrame = 0;
      this._idleHandle = 0;
      this._idleTimer  = 0;

      this._abort        = new AbortController();
      this._dragDocAbort = null;
      this._eventsBound  = false;

      // One frame so the grid has laid out before we measure
      requestAnimationFrame(() => this._init());
    }

    /**
     * Rebuild after Hello Retail (or any other source) replaces the original cards.
     * Deferred by a frame so the new cards have laid out before we measure them.
     */
    refresh() {
      if (!this._track) return;

      cancelAnimationFrame(this._refreshFrame);
      this._refreshFrame = requestAnimationFrame(() => {
        this._refreshFrame = 0;
        this._removeClones();
        this._originals = this._cells().filter((cell) => !cell.hasAttribute(CLONE_ATTRIBUTE));
        this._track.scrollLeft = 0;

        // The cards are new, so the loop geometry measured for the old set is
        // meaningless. Left in place, _build() would read _currentIndex() from
        // the stale origin/span while scrollLeft is 0, get a negative offset,
        // and park the track several cards in - the first card (or a blurb at
        // slot 2) then sits off-screen to the left after Hello Retail renders.
        this._looping = false;
        this._origin  = 0;
        this._span    = 0;
        this._anchor  = null;

        this._observeResize();

        if (this._originals.length > 1) {
          this._bindEvents();
        }

        // Controls reflect the new cards straight away; the clone padding is
        // several full-card `cloneNode(true)` calls and lands on the same frame
        // the recommendations paint, so it waits for an idle moment instead.
        this._updateButtons();
        this._scheduleRebuild();
      });
    }

    /**
     * Blocks that are not the selected tab are `display: none`, so their track
     * has no box to measure. Building from those zeroes would drop the loop
     * padding and hide the controls, so the work waits until the block is shown
     * — either through the resize observer or the carousel's settle event.
     */
    _rebuild() {
      if (!this._hasLayout()) {
        this._pendingBuild = true;
        return;
      }

      this._pendingBuild = false;
      this._build();
      this._updateButtons();
    }

    /**
     * Defers the clone padding to an idle moment. Nothing the user can interact
     * with depends on it, and building it inline competes with the card images
     * for the same frames.
     */
    _scheduleRebuild() {
      this._cancelScheduledRebuild();

      const run = () => {
        this._idleHandle = 0;
        this._idleTimer  = 0;
        this._rebuild();
      };

      if (typeof requestIdleCallback === 'function') {
        this._idleHandle = requestIdleCallback(run, { timeout: 300 });
      } else {
        this._idleTimer = setTimeout(run, 50);
      }
    }

    _cancelScheduledRebuild() {
      if (this._idleHandle && typeof cancelIdleCallback === 'function') {
        cancelIdleCallback(this._idleHandle);
      }
      clearTimeout(this._idleTimer);
      this._idleHandle = 0;
      this._idleTimer  = 0;
    }

    /** Runs a deferred build immediately when something needs the geometry now. */
    _flushScheduledRebuild() {
      if (!this._idleHandle && !this._idleTimer) return;
      this._cancelScheduledRebuild();
      this._rebuild();
    }

    _hasLayout() {
      return this._track.clientWidth > 0;
    }

    disconnectedCallback() {
      this._abort.abort();
      this._dragDocAbort?.abort();
      this._resizeObserver?.disconnect();
      cancelAnimationFrame(this._wrapFrame);
      cancelAnimationFrame(this._refreshFrame);
      this._cancelScheduledRebuild();
      clearTimeout(this._scrollTimer);
      clearTimeout(this._scrollGuard);
      clearTimeout(this._resizeTimer);
    }

    /** Infinite looping is opt-in through the `loop` attribute. */
    get loopRequested() {
      return this.hasAttribute('loop');
    }

    _init() {
      this._observeResize();

      if (this._originals.length < 2) {
        this._updateButtons();
        return;
      }

      this._bindEvents();
      this._updateButtons();
      this._scheduleRebuild();
    }

    // ── Geometry ─────────────────────────────────────────────────────────────

    /** Track children that actually lay out — the cards, clones included. */
    _cells() {
      return Array.from(this._track.children).filter((child) => !NON_RENDERED_TAGS.has(child.tagName));
    }

    /**
     * Scroll offset that brings `element` to the resting position of the first
     * cell. The distance between two cells does not change while scrolling, so
     * this is a stable coordinate we can compare `scrollLeft` against.
     */
    _scrollOffsetOf(element, anchor = this._anchor) {
      if (!element || !anchor) return 0;
      return element.getBoundingClientRect().left - anchor.getBoundingClientRect().left;
    }

    /** Width of one card slot (card + gap), signed by the reading direction. */
    _measureSlot(cells) {
      if (cells.length === 0) return 0;

      if (cells.length > 1) {
        const slot = this._scrollOffsetOf(cells[1], cells[0]);
        if (Math.abs(slot) > 1) return slot;
      }

      const gap = parseFloat(getComputedStyle(this._track).columnGap) || 0;
      const slot = cells[0].offsetWidth + gap;
      return slot > 1 ? slot : 0;
    }

    // ── Loop construction ────────────────────────────────────────────────────

    _removeClones() {
      this._track.querySelectorAll(`:scope > [${CLONE_ATTRIBUTE}]`).forEach((clone) => clone.remove());
    }

    /**
     * (Re)builds the clone padding around the original cards and parks the track
     * on the card that was previously in view.
     */
    _build() {
      const track = this._track;
      const previousIndex = this._currentIndex();

      this._removeClones();
      this._looping = false;
      this._origin  = 0;
      this._span    = 0;

      const cells = this._cells();
      this._anchor = cells[0] ?? null;
      this._slot = this._measureSlot(cells);
      this._viewportWidth = track.clientWidth;

      const originals = this._originals;
      const count = originals.length;

      if (!this.loopRequested || this._slot === 0 || count < 2) return;

      // A set that fits on screen has nothing to loop through: padding it with
      // clones would fake an overflow, and the cards would repeat side by side.
      if (Math.abs(this._slot) * count <= track.clientWidth + 1) return;

      // The padding is sized off the viewport, so it always leaves at least one
      // card of runway past the loop window even when a short set fits on screen.
      const edge = Math.ceil(track.clientWidth / Math.abs(this._slot)) + EDGE_BUFFER;

      // Cards are repeated modulo the set size, so a set shorter than the
      // padding still tiles correctly.
      const leading = document.createDocumentFragment();
      for (let i = edge; i >= 1; i--) {
        leading.appendChild(this._createClone(originals[(count - (i % count)) % count], `l${i}`));
      }

      const trailing = document.createDocumentFragment();
      for (let i = 0; i < edge; i++) {
        trailing.appendChild(this._createClone(originals[i % count], `t${i}`));
      }
      const firstTrailingClone = trailing.firstChild;

      track.insertBefore(leading, originals[0]);
      track.appendChild(trailing);

      this._anchor = this._cells()[0] ?? null;
      this._origin = this._scrollOffsetOf(originals[0]);
      this._span   = this._scrollOffsetOf(firstTrailingClone) - this._origin;

      if (Math.abs(this._span) < 1) {
        this._removeClones();
        this._origin = 0;
        this._span   = 0;
        return;
      }

      this._looping = true;
      this._jumpTo(this._origin + previousIndex * this._slot);
    }

    /**
     * Clones a card for the loop padding. Ids, the attributes referencing them
     * and swatch radio groups are namespaced so a copy can never hijack the
     * original, and the copy is hidden from assistive tech and keyboard
     * navigation while staying clickable with a pointer.
     */
    _createClone(source, key) {
      const suffix = `fcloop-${this._uid}-${key}`;
      const clone  = source.cloneNode(true);
      const ids    = new Map();

      clone.setAttribute(CLONE_ATTRIBUTE, '');
      clone.setAttribute('aria-hidden', 'true');

      if (clone.id) {
        ids.set(clone.id, `${clone.id}-${suffix}`);
        clone.id = ids.get(clone.id);
      }

      clone.querySelectorAll('[id]').forEach((element) => {
        const renamed = `${element.id}-${suffix}`;
        ids.set(element.id, renamed);
        element.id = renamed;
      });

      if (ids.size > 0) {
        ID_REFERENCE_ATTRIBUTES.forEach((attribute) => {
          clone.querySelectorAll(`[${attribute}]`).forEach((element) => {
            const value = element
              .getAttribute(attribute)
              .split(/\s+/)
              .map((token) => ids.get(token) ?? token)
              .join(' ');
            element.setAttribute(attribute, value);
          });
        });
      }

      // Swatch radios sit outside any form; sharing a name with the original
      // would let the clone steal its checked state. Inputs inside a form carry
      // the add-to-cart payload and must keep their names.
      clone.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach((input) => {
        if (input.name && !input.closest('form')) input.name = `${input.name}-${suffix}`;
      });

      // Reveal animations are driven by the original cards only.
      clone.removeAttribute('reveal-on-scroll');
      clone.querySelectorAll('[reveal-on-scroll]').forEach((element) => {
        element.removeAttribute('reveal-on-scroll');
      });

      // Padding cards start off-screen, so they must never compete for bandwidth
      // with the originals.
      clone.querySelectorAll('img').forEach((image) => {
        image.loading = 'lazy';
        image.fetchPriority = 'low';
      });

      clone.querySelectorAll('a[href], button, input, select, textarea, [tabindex]').forEach((element) => {
        element.tabIndex = -1;
      });

      return clone;
    }

    // ── Loop bookkeeping ─────────────────────────────────────────────────────

    /** Folds a scroll offset back into the window [origin, origin + span). */
    _wrap(offset) {
      if (!this._looping) return offset;
      // The epsilon keeps floating point noise from costing a whole lap.
      const laps = Math.floor((offset - this._origin) / this._span + 1e-4);
      return offset - laps * this._span;
    }

    _normalize() {
      if (!this._looping) return;
      const current = this._track.scrollLeft;
      const wrapped = this._wrap(current);
      if (Math.abs(wrapped - current) > 0.5) this._jumpTo(wrapped);
    }

    /** Repositions the track without animating, whatever `scroll-behavior` says. */
    _jumpTo(offset) {
      const track = this._track;
      const behavior = track.style.scrollBehavior;
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = offset;
      track.style.scrollBehavior = behavior;
    }

    /** Index of the original card currently at the leading edge. */
    _currentIndex() {
      if (!this._looping || this._slot === 0) return 0;
      const count = this._originals.length;
      const position = Math.round((this._track.scrollLeft - this._origin) / this._slot);
      return ((position % count) + count) % count;
    }

    /**
     * True when the viewport is close enough to a physical end of the track that
     * carrying on would hit the wall. Rewinding cancels momentum scrolling, so
     * it is only worth doing mid-scroll once we get here.
     */
    _nearTrackEdge() {
      const track = this._track;
      const position = Math.abs(track.scrollLeft);
      const limit = track.scrollWidth - track.clientWidth;
      const guard = Math.max(track.clientWidth * 0.5, Math.abs(this._slot));
      return position < guard || position > limit - guard;
    }

    // ── Controls ─────────────────────────────────────────────────────────────

    _updateButtons() {
      // Never decide visibility from a block that has no layout yet.
      if (!this._hasLayout()) return;

      const track = this._track;
      // Use a 2px tolerance to absorb subpixel rounding differences
      const limit = track.scrollWidth - track.clientWidth;

      // Fewer cards than fit in one view: there is nowhere to scroll, so the
      // controls are hidden rather than shown permanently disabled.
      const scrollable = this._looping || limit > 2;

      if (this._prevBtn) this._prevBtn.hidden = !scrollable;
      if (this._nextBtn) this._nextBtn.hidden = !scrollable;

      if (!scrollable) return;

      // A looping carousel can always be scrolled in either direction.
      if (this._looping) {
        if (this._prevBtn) this._prevBtn.disabled = false;
        if (this._nextBtn) this._nextBtn.disabled = false;
        return;
      }

      const position = Math.abs(track.scrollLeft);

      if (this._prevBtn) this._prevBtn.disabled = position <= 2;
      if (this._nextBtn) this._nextBtn.disabled = position >= limit - 2;
    }

    /** Scrolls by exactly one card in the given direction. */
    _scrollByCard(direction) {
      // A click before the deferred build has run still needs the geometry.
      if (this._slot === 0) this._flushScheduledRebuild();
      if (this._scrolling || this._slot === 0) return;

      const track = this._track;

      // Rewind first: the padding guarantees a full card of runway on both sides
      // of the loop window, so the smooth animation below can never need a
      // mid-flight wrap that would cut it short.
      this._normalize();

      const before = track.scrollLeft;

      this._scrolling = true;
      track.scrollBy({ left: direction * this._slot, behavior: 'smooth' });

      // Guard: if scrollBy was a no-op (already at edge) scrollend never fires
      // and _scrolling would be stuck. Detect it after 80 ms and unlock.
      clearTimeout(this._scrollGuard);
      this._scrollGuard = setTimeout(() => {
        if (!this._scrolling) return;
        if (Math.round(track.scrollLeft) !== Math.round(before)) return;
        this._scrolling = false;
        this._updateButtons();
      }, 80);
    }

    _bindEvents() {
      if (this._eventsBound) return;
      this._eventsBound = true;
      const { signal } = this._abort;

      const onScrollEnd = () => {
        clearTimeout(this._scrollTimer);
        clearTimeout(this._scrollGuard);
        if (this._dragging) return;
        this._track.style.removeProperty('scroll-snap-type');
        this._scrolling = false;
        // At rest the rewind is imperceptible, so this is where the loop resets.
        this._normalize();
        this._updateButtons();
      };

      // Native scrollend (Chrome 114+, Firefox 109+)
      this._track.addEventListener('scrollend', onScrollEnd, { signal });

      // Fallback for Safari and older browsers
      this._track.addEventListener(
        'scroll',
        () => {
          this._scheduleEdgeWrap();
          clearTimeout(this._scrollTimer);
          this._scrollTimer = setTimeout(onScrollEnd, 200);
        },
        { signal, passive: true }
      );

      this._prevBtn?.addEventListener('click', () => this._scrollByCard(-1), { signal });
      this._nextBtn?.addEventListener('click', () => this._scrollByCard(1), { signal });

      // A touch scroll also needs the padding in place, so a deferred build is
      // pulled forward as soon as the user reaches for the track.
      this._track.addEventListener('pointerdown', () => this._flushScheduledRebuild(), { signal, passive: true });

      this._bindDrag();
    }

    /**
     * Rewinds during a native (touch or wheel) scroll, but only when the track is
     * about to run out of clones. Programmatic scrolls and drags reposition
     * themselves and must not be disturbed.
     */
    _scheduleEdgeWrap() {
      if (!this._looping || this._scrolling || this._dragging || this._wrapFrame) return;

      this._wrapFrame = requestAnimationFrame(() => {
        this._wrapFrame = 0;
        if (this._scrolling || this._dragging) return;
        if (this._nearTrackEdge()) this._normalize();
      });
    }

    // ── Mouse-drag-to-scroll ─────────────────────────────────────────────────
    // Disables CSS snap while dragging so scrollLeft tracks the cursor exactly,
    // then snaps to the nearest card on release.
    _bindDrag() {
      const track      = this._track;
      const { signal } = this._abort;
      let dragStart    = null; // { x, left }
      let dragged      = false;

      const onMouseMove = (e) => {
        if (!dragStart) return;
        e.preventDefault();
        const dx = e.clientX - dragStart.x;
        track.scrollLeft = dragStart.left - dx;

        // Direct manipulation has no momentum to preserve, so the loop can be
        // rewound as soon as the cursor crosses the window.
        if (this._looping) {
          const wrapped = this._wrap(track.scrollLeft);
          const shift = wrapped - track.scrollLeft;
          if (Math.abs(shift) > 0.5) {
            dragStart.left += shift;
            this._jumpTo(wrapped);
          }
        }

        if (dx !== 0) dragged = true;
      };

      const onMouseUp = () => {
        this._dragDocAbort?.abort();
        this._dragDocAbort = null;

        if (!dragged) {
          track.style.removeProperty('scroll-snap-type');
          this._dragging = false;
          dragStart = null;
          return;
        }

        // Snap to the nearest card boundary
        const nearest =
          this._slot !== 0
            ? this._origin + Math.round((track.scrollLeft - this._origin) / this._slot) * this._slot
            : track.scrollLeft;

        this._dragging = false;

        if (Math.abs(track.scrollLeft - nearest) < 2) {
          track.style.removeProperty('scroll-snap-type');
          this._scrolling = false;
          this._normalize();
          this._updateButtons();
        } else {
          this._scrolling = true;
          track.scrollTo({ left: nearest, behavior: 'smooth' });
          // scrollend → onScrollEnd handles the rest
        }

        dragStart = null;
      };

      track.addEventListener(
        'mousedown',
        (e) => {
          if (e.button !== 0) return;
          dragStart      = { x: e.clientX, left: track.scrollLeft };
          dragged        = false;
          this._dragging = true;
          track.style.setProperty('scroll-snap-type', 'none');
          this._dragDocAbort = new AbortController();
          const { signal: docSig } = this._dragDocAbort;
          document.addEventListener('mousemove', onMouseMove, { signal: docSig });
          document.addEventListener('mouseup',   onMouseUp,   { signal: docSig });
          // Prevent card links from firing on a drag
          track.addEventListener(
            'click',
            (ce) => {
              if (dragged) ce.preventDefault();
            },
            { once: true }
          );
        },
        { signal }
      );
    }

    // ── Responsiveness ───────────────────────────────────────────────────────
    // Card width and the number of visible cards change at breakpoints, which
    // changes how much padding the loop needs.
    _observeResize() {
      if (typeof ResizeObserver === 'undefined' || this._resizeObserver) return;

      this._resizeObserver = new ResizeObserver(() => {
        clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => this._onResize(), 150);
      });
      this._resizeObserver.observe(this._track);
    }

    _onResize() {
      if (!this.isConnected || !this._hasLayout()) return;

      const width = this._track.clientWidth;
      const slot  = this._measureSlot(this._cells());
      if (!this._pendingBuild && width === this._viewportWidth && Math.abs(slot - this._slot) < 0.5) return;

      this._cancelScheduledRebuild();
      this._rebuild();
    }
  }

  if (!customElements.get('fc-loop-carousel')) {
    customElements.define('fc-loop-carousel', FcLoopCarousel);
  }

  const HR_ORIGIN = 'https://core.helloretail.com';
  const HR_RECOMS_ENDPOINT = `${HR_ORIGIN}/serve/recoms`;
  const HR_FIELDS = ['title', 'originalUrl', 'trackingCode', 'productNumber', 'extraData'];

  /** Products per Section Rendering request, kept under the search page size. */
  const HR_SEARCH_CHUNK = 20;

  /**
   * How long the recommendations request is willing to wait for the Hello Retail
   * SDK to hand over a tracking id. Without an id Hello Retail cannot attribute
   * impressions, clicks or personalisation to the visitor, so the wait is long
   * enough for the SDK to load on a first visit. Returning visitors skip the wait
   * entirely through the cookie below. The request still goes out without an id
   * when the SDK is consent-gated or missing, rather than hiding the carousel.
   * (Claude 21.09: raised from 300 ms.)
   */
  const HR_TRACKING_TIMEOUT = 800;

  /**
   * Cookies the SDK may have already written on a previous visit. Reading one
   * skips the SDK wait completely. VERIFY THESE NAMES against a real session in
   * devtools (Application → Cookies) and trim the list to the one that exists —
   * an unknown name simply falls through to the SDK path.
   */
  const HR_TRACKING_COOKIES = ['hrTrackingUserId', 'hello_retail_id', 'hrTrackingUserID', 'hr_tracking_user_id'];

  /**
   * Cards visible in one carousel at once — 2 below 700 px, 4 at 700 px and above, matching
   * --product-list-items-per-row in sections/featured-collections.liquid. Only these get eager,
   * high-priority images, and only when the carousel is on screen at swap time (Claude 26.09.2026).
   * Previously the first 4 cards of EVERY carousel, badges included, were forced to
   * loading=eager + fetchpriority=high, however far down the page the carousel sat — about 40
   * images competing with what the visitor actually sees.
   */
  const HR_VISIBLE_CARDS_DESKTOP = 4;
  const HR_VISIBLE_CARDS_MOBILE = 2;

  /**
   * DEFERRED LOADING (Claude 26.09.2026). A carousel further than this many screen heights below
   * the visible area does not ask Hello Retail for recommendations until the visitor scrolls within
   * that distance. Until then it shows its server-rendered fallback products, exactly as it does
   * today while the request is in flight. Carousels near the top are still fetched immediately, in
   * one batched request as before.
   *
   * Sections that stay hidden until loaded (data-hello-retail-hide-section, e.g. "Du kanske också
   * gillar" on the product page) are deferred too: the .shopify-section wrapper still has a position
   * on the page even while its content is hidden, so it is what gets observed. The recommendations
   * are fetched well before the visitor reaches the spot, so the section appears below the screen.
   *
   * Effect on Hello Retail statistics: carousels a visitor never gets close to are no longer
   * requested, so impressions drop to what was actually near the screen.
   */
  const HR_PREFETCH_SCREENS = 1.5;


  /**
   * This script is deferred, so the document is fully parsed by the time it runs
   * — but `readyState` still reads "loading" until every other deferred script
   * has finished, so waiting for DOMContentLoaded means queueing behind all of
   * them. Run now, and run again on DOMContentLoaded only as a safety net for
   * the case where the script is loaded some other way. Both entry points are
   * idempotent.
   */
  function hrOnReady(callback) {
    callback();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    }
  }

  function hrSectionId(element) {
    const section = element.closest('.shopify-section');
    return section ? section.id.replace('shopify-section-', '') : '';
  }

  function hrHandleFromUrl(url) {
    if (!url) return '';
    try {
      const path = new URL(url, window.location.origin).pathname;
      const match = path.match(/\/products\/([^/]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch {
      return '';
    }
  }

  function hrExtraProductId(product) {
    const extra = product.extraData || {};
    const candidates = [
      extra.productId,
      extra.product_id,
      extra.shopifyProductId,
      extra.shopify_product_id,
      extra.ProductId,
    ];
    const found = candidates.find((value) => value != null && String(value).trim() !== '');
    return found ? String(found) : '';
  }

  function hrProductHandle(product) {
    const extra = product.extraData || {};
    if (extra.handle) return String(extra.handle);
    return hrHandleFromUrl(product.originalUrl) || hrHandleFromUrl(product.url);
  }

  /** Stable identity for a recommended product across the response and lookups. */
  function hrProductKey(product) {
    return String(product.productNumber || product.originalUrl || product.url || '');
  }

  let hrTrackingIdPromise = null;

  function hrCookieTrackingId() {
    if (!document.cookie) return '';
    for (const name of HR_TRACKING_COOKIES) {
      const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
      if (match && match[1]) return decodeURIComponent(match[1]);
    }
    return '';
  }

  /**
   * Resolves to a tracking id, or to an empty string once the timeout elapses.
   * Started eagerly at script execution so the clock runs in parallel with the
   * rest of the page rather than in front of the recommendations request.
   */
  function hrTrackingUserId() {
    if (hrTrackingIdPromise) return hrTrackingIdPromise;

    const cookieId = hrCookieTrackingId();
    if (cookieId) {
      hrTrackingIdPromise = Promise.resolve(cookieId);
      return hrTrackingIdPromise;
    }

    hrTrackingIdPromise = new Promise((resolve) => {
      let settled = false;
      const finish = (id) => {
        if (settled) return;
        settled = true;
        resolve(id || '');
      };

      const timeout = setTimeout(() => finish(''), HR_TRACKING_TIMEOUT);

      // Documented SDK command: hrq.push(["getTrackingUserId", callback]).
      // https://developer.helloretail.com/sdk/tracking/tracking_user/
      // (Claude 21.09: replaced an undocumented function push that never
      // returned an id.)
      window.hrq = window.hrq || [];
      window.hrq.push([
        'getTrackingUserId',
        function (trackingUserId) {
          clearTimeout(timeout);
          finish(trackingUserId);
        },
      ]);
    });

    return hrTrackingIdPromise;
  }

  async function hrFetchRecoms(requests, trackingUserId) {
    const body = { trackingUserId: trackingUserId || '', requests };
    const response = await fetch(HR_RECOMS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`Hello Retail ${response.status}`);
    return response.json();
  }

  async function hrShopifyProduct(handle) {
    const root = window.Shopify?.routes?.root || '/';
    try {
      const response = await fetch(`${root}products/${handle}.js`);
      if (!response.ok) return null;
      const product = await response.json();
      if (!product?.id) return null;
      return { id: String(product.id), handle: product.handle || handle };
    } catch {
      return null;
    }
  }

  /**
   * Turns recommendations into numeric product ids for the Section Rendering
   * query.
   *
   * The `/products/{handle}.js` round trip exists only to translate a handle
   * into an id, so it is pure latency whenever the feed already carries one:
   * `extraData` is checked first and, when it is populated, this stage costs
   * nothing at all. If it is not populated, adding the Shopify product id to the
   * Hello Retail feed export is the cheapest win available here.
   *
   * `productNumber` stays last: the context attribute this theme sends is
   * `variant.id,product.id`, so a numeric productNumber may well be a variant id
   * and would silently match nothing in an `id:` search.
   */
  async function hrResolveProductIds(hrProducts) {
    const resolved = [];
    const missing = [];

    hrProducts.forEach((product) => {
      const handle = hrProductHandle(product);
      const extraId = hrExtraProductId(product);
      const number = product.productNumber != null ? String(product.productNumber) : '';

      if (/^\d+$/.test(extraId)) {
        resolved.push({ product, id: extraId, handle });
      } else if (handle) {
        missing.push({ product, handle, extraId, number });
      } else if (extraId) {
        resolved.push({ product, id: extraId, handle: '' });
      } else if (/^\d+$/.test(number)) {
        resolved.push({ product, id: number, handle: '' });
      }
    });

    if (missing.length === 0) return resolved;

    const fetched = await Promise.all(
      missing.map(async ({ product, handle, extraId, number }) => {
        const shopifyProduct = await hrShopifyProduct(handle);
        const id = shopifyProduct?.id || extraId || (/^\d+$/.test(number) ? number : '');
        if (!id) return null;
        return { product, id, handle: shopifyProduct?.handle || handle };
      })
    );

    return resolved.concat(fetched.filter(Boolean));
  }

  function hrCollectCards(doc, cardsByHandle) {
    const source = doc.querySelector('[data-hr-product-cards]') || doc;

    source.querySelectorAll('[data-hr-card]').forEach((wrap) => {
      const card = wrap.querySelector('product-card');
      if (!card) return;
      const imported = document.importNode(card, true);
      const handle = wrap.getAttribute('data-product-handle') || imported.getAttribute('handle') || '';
      const id = wrap.getAttribute('data-product-id') || '';
      if (handle && !cardsByHandle.has(handle)) cardsByHandle.set(handle, imported);
      if (id && !cardsByHandle.has(`id:${id}`)) cardsByHandle.set(`id:${id}`, imported);
    });

    source.querySelectorAll('product-card[handle]').forEach((card) => {
      const handle = card.getAttribute('handle');
      if (handle && !cardsByHandle.has(handle)) {
        cardsByHandle.set(handle, document.importNode(card, true));
      }
    });
  }

  /**
   * Section Rendering is the slowest hop in this chain, so the chunks go out in
   * parallel: a slot with more than `HR_SEARCH_CHUNK` products used to pay two
   * full round trips back to back.
   */
  async function hrFetchProductCards(sectionId, productIds) {
    const cardsByHandle = new Map();
    if (!sectionId || productIds.length === 0) return cardsByHandle;

    const root = window.Shopify?.routes?.root || '/';
    const chunks = [];
    for (let index = 0; index < productIds.length; index += HR_SEARCH_CHUNK) {
      chunks.push(productIds.slice(index, index + HR_SEARCH_CHUNK));
    }

    const documents = await Promise.all(
      chunks.map(async (chunk) => {
        const query = chunk.map((id) => `id:${id}`).join(' OR ');
        const url = `${root}search?type=product&q=${encodeURIComponent(query)}&section_id=${encodeURIComponent(sectionId)}`;
        try {
          const response = await fetch(url);
          if (!response.ok) return null;
          return new DOMParser().parseFromString(await response.text(), 'text/html');
        } catch {
          return null;
        }
      })
    );

    documents.forEach((doc) => {
      if (doc) hrCollectCards(doc, cardsByHandle);
    });

    return cardsByHandle;
  }

  /**
   * The card keeps the storefront's own product URL — the recommendation URLs are
   * absolute and point at the myshopify domain, which would break relative
   * navigation and the theme's link prefetching. The tracking code is kept on
   * the card instead and reported by hrBindClickTracking() below.
   */
  function hrTagCard(card, hrProduct) {
    if (hrProduct.trackingCode) card.setAttribute('data-hr-tracking-code', hrProduct.trackingCode);
  }

  function hrVisibleCardCount() {
    return window.matchMedia('(min-width: 700px)').matches ? HR_VISIBLE_CARDS_DESKTOP : HR_VISIBLE_CARDS_MOBILE;
  }

  /** True when the element has a box and at least part of it is inside the viewport. */
  function hrIsOnScreen(element) {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return false;
    return rect.bottom > 0 && rect.top < window.innerHeight;
  }

  /**
   * Cards arrive from Section Rendering with lazy images. When the carousel is on screen at swap
   * time, the cards the visitor can actually see (2 on mobile, 4 on desktop) get eager,
   * high-priority images — badges included, so badge and product appear together — because lazy
   * loading there would add another round trip to a wait the visitor is already sitting through.
   * The hover image stays lazy. Carousels below the fold keep lazy loading entirely.
   */
  function hrPrioritizeImages(nodes, item) {
    if (!hrIsOnScreen(item)) return;

    nodes.slice(0, hrVisibleCardCount()).forEach((node) => {
      node.querySelectorAll('img').forEach((image) => {
        if (image.classList.contains('product-card__image--secondary')) return;
        image.loading = 'eager';
        image.fetchPriority = 'high';
      });
    });
  }

  function hrProductList(item) {
    return (
      item.querySelector('[data-hello-retail-list]') ||
      item.querySelector('[data-track]') ||
      item.querySelector('product-list')
    );
  }

  function hrContextValue(slot, attribute) {
    return (
      slot.getAttribute(attribute) ||
      slot.closest(`[${attribute}]`)?.getAttribute(attribute) ||
      slot.closest('featured-collections-carousel')?.getAttribute(attribute) ||
      ''
    );
  }

  function hrRequestContext(slot) {
    const context = {};
    const url = hrContextValue(slot, 'data-hello-retail-product-url').trim();
    if (url) context.urls = [url];

    const numbers = hrContextValue(slot, 'data-hello-retail-product-numbers')
      .split(',')
      .map((value) => value.trim())
      .filter((value, index, list) => value && list.indexOf(value) === index);

    if (numbers.length > 0) context.productNumbers = numbers;

    return context.urls || context.productNumbers ? context : null;
  }

  function hrSectionElement(slot) {
    return slot.closest('.shopify-section');
  }

  function hrShouldHideSection(slot) {
    return slot.hasAttribute('data-hello-retail-hide-section');
  }

  /** The carousel sits inside the slot, so it is a descendant and never an ancestor. */
  function hrRefreshCarousel(item) {
    item.querySelector('fc-loop-carousel')?.refresh?.();
    const scrollCarousel = item.querySelector('scroll-carousel');
    if (typeof scrollCarousel?.select === 'function') {
      scrollCarousel.select(0, { instant: true });
    }
  }

  function hrClearProductCards(list) {
    Array.from(list.children).forEach((child) => {
      if (child.tagName === 'PRODUCT-CARD' || child.hasAttribute('data-fc-clone')) {
        child.remove();
      }
    });
  }

  function hrHideEmptySlot(item) {
    item.setAttribute('aria-busy', 'false');

    // Rutenettet ved omtalene (Claude 09.10.2026): beholder de server-renderte bestselgerne når Hello
    // Retail svarer tomt eller feiler, i stedet for å skjule seksjonen. Se sections/related-products.liquid.
    if (item.hasAttribute('data-hello-retail-keep-fallback')) {
      item.hidden = false;
      return;
    }

    if (hrShouldHideSection(item)) {
      item.hidden = true;
      const section = hrSectionElement(item);
      if (section) section.style.display = 'none';
      return;
    }

    item.hidden = true;
    const carousel = item.closest('featured-collections-carousel');
    if (carousel && !carousel.querySelector('.featured-collections-carousel__item:not([hidden])')) {
      const section = carousel.closest('.shopify-section');
      if (section) section.style.display = 'none';
    }
  }

  function hrRenderSlot(item, matches, cardsByHandle) {
    const list = hrProductList(item);
    if (!list) return;

    const nodes = [];
    matches.forEach(({ product, handle, id }) => {
      const card = (handle && cardsByHandle.get(handle)) || (id && cardsByHandle.get(`id:${id}`)) || null;
      if (!card) return;
      const clone = card.cloneNode(true);
      hrTagCard(clone, product);
      nodes.push(clone);
    });

    if (nodes.length === 0) {
      hrHideEmptySlot(item);
      return;
    }

    hrPrioritizeImages(nodes, item);

    // One insertion instead of one per card keeps the swap to a single layout.
    const fragment = document.createDocumentFragment();
    nodes.forEach((node) => fragment.appendChild(node));

    hrClearProductCards(list);
    list.appendChild(fragment);
    // New cards are inserted after Lipscore's onload sweep. Re-init the widgets.
    if (typeof window.mmLipscoreScan === 'function') {
      window.mmLipscoreScan();
    }
    item.removeAttribute('hidden');
    item.setAttribute('aria-busy', 'false');

    if (item.hasAttribute('data-hello-retail-hide-section')) {
      const section = hrSectionElement(item);
      if (section) section.style.display = '';
    }

    hrRefreshCarousel(item);
  }

  /**
   * Fetches and renders the given slots (all pending slots when called without an argument).
   * Slots passed together share one recommendations request, as before.
   */
  async function loadHelloRetailFeaturedCollections(requestedSlots) {
    const candidates = requestedSlots || Array.from(document.querySelectorAll('[data-hello-retail-key]'));
    const slots = candidates.filter((slot) => {
      return slot.getAttribute('data-hello-retail-key').trim() && !slot.hasAttribute('data-hr-ready');
    });
    if (slots.length === 0) return;

    slots.forEach((slot) => {
      slot.setAttribute('data-hr-ready', '');
      if (hrShouldHideSection(slot)) {
        const section = hrSectionElement(slot);
        if (section) section.style.display = 'none';
      }
    });

    const requests = slots.map((slot) => {
      const request = {
        key: slot.getAttribute('data-hello-retail-key').trim(),
        format: 'json',
        fields: HR_FIELDS,
        deviceType: window.matchMedia('(min-width: 700px)').matches ? 'DESKTOP' : 'MOBILE',
      };

      const context = hrRequestContext(slot);
      if (context) request.context = context;

      return request;
    });

    let payload;
    try {
      // Started at script execution, so this is normally already settled.
      const trackingUserId = await hrTrackingUserId();
      payload = await hrFetchRecoms(requests, trackingUserId);
    } catch (error) {
      console.warn('Hello Retail recommendations failed', error);
      slots.forEach(hrHideEmptySlot);
      return;
    }

    const responses = Array.isArray(payload?.responses) ? payload.responses : [];
    const slotProducts = slots.map((slot, index) => {
      const response = responses[index];
      const products = response?.success && Array.isArray(response.products) ? response.products : [];
      return { slot, products };
    });

    const uniqueHrProducts = [];
    const seen = new Set();
    slotProducts.forEach(({ products }) => {
      products.forEach((product) => {
        const key = hrProductKey(product);
        if (!key || seen.has(key)) return;
        seen.add(key);
        uniqueHrProducts.push(product);
      });
    });

    let resolved;
    try {
      resolved = await hrResolveProductIds(uniqueHrProducts);
    } catch (error) {
      console.warn('Hello Retail product lookup failed', error);
      slots.forEach(hrHideEmptySlot);
      return;
    }

    const resolvedByKey = new Map();
    resolved.forEach((entry) => {
      const key = hrProductKey(entry.product);
      if (key) resolvedByKey.set(key, entry);
    });

    // Card markup depends on section settings, so each section renders its own.
    const idsBySection = new Map();
    slotProducts.forEach(({ slot, products }) => {
      const sectionId = hrSectionId(slot);
      if (!idsBySection.has(sectionId)) idsBySection.set(sectionId, new Set());
      const ids = idsBySection.get(sectionId);
      products.forEach((product) => {
        const entry = resolvedByKey.get(hrProductKey(product));
        if (entry) ids.add(entry.id);
      });
    });

    const cardsBySection = new Map();
    await Promise.all(
      Array.from(idsBySection, async ([sectionId, ids]) => {
        cardsBySection.set(sectionId, await hrFetchProductCards(sectionId, Array.from(ids)));
      })
    );

    slotProducts.forEach(({ slot, products }) => {
      if (products.length === 0) {
        hrHideEmptySlot(slot);
        return;
      }

      const matches = products.map((product) => resolvedByKey.get(hrProductKey(product))).filter(Boolean);

      hrRenderSlot(slot, matches, cardsBySection.get(hrSectionId(slot)) || new Map());
    });
  }

  // ── Deferred loading (Claude 26.09.2026) ─────────────────────────────────────
  // See HR_PREFETCH_SCREENS. The section is observed rather than the slot: slots in tabs that are
  // not selected are display:none, and hide-section slots are hidden until loaded, so neither would
  // ever intersect — but their .shopify-section wrapper does.

  let hrDeferredObserver = null;
  const hrDeferredSlots = new Map(); // section element -> slots waiting for it

  function hrObservationTarget(slot) {
    return hrSectionElement(slot) || slot;
  }

  /** Within the prefetch distance of the viewport (a box-less element counts as near). */
  function hrIsNearViewport(element) {
    const rect = element.getBoundingClientRect();
    const margin = window.innerHeight * HR_PREFETCH_SCREENS;
    return rect.top < window.innerHeight + margin && rect.bottom > -margin;
  }

  function hrDeferredObserverInstance() {
    if (hrDeferredObserver) return hrDeferredObserver;

    hrDeferredObserver = new IntersectionObserver(
      (entries) => {
        const ready = [];

        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          hrDeferredObserver.unobserve(entry.target);
          const waiting = hrDeferredSlots.get(entry.target) || [];
          hrDeferredSlots.delete(entry.target);
          waiting.forEach((slot) => {
            slot.removeAttribute('data-hr-deferred');
            ready.push(slot);
          });
        });

        if (ready.length > 0) loadHelloRetailFeaturedCollections(ready);
      },
      { rootMargin: `${HR_PREFETCH_SCREENS * 100}% 0px` }
    );

    return hrDeferredObserver;
  }

  /** Fetches near slots now (one batched request) and defers the rest until they come close. */
  function hrScheduleSlots() {
    const pending = Array.from(document.querySelectorAll('[data-hello-retail-key]')).filter((slot) => {
      return (
        slot.getAttribute('data-hello-retail-key').trim() &&
        !slot.hasAttribute('data-hr-ready') &&
        !slot.hasAttribute('data-hr-deferred')
      );
    });
    if (pending.length === 0) return;

    if (typeof IntersectionObserver === 'undefined') {
      loadHelloRetailFeaturedCollections(pending);
      return;
    }

    const now = [];
    pending.forEach((slot) => {
      const target = hrObservationTarget(slot);

      if (hrIsNearViewport(target)) {
        now.push(slot);
        return;
      }

      slot.setAttribute('data-hr-deferred', '');
      if (!hrDeferredSlots.has(target)) hrDeferredSlots.set(target, []);
      hrDeferredSlots.get(target).push(slot);
      hrDeferredObserverInstance().observe(target);
    });

    if (now.length > 0) loadHelloRetailFeaturedCollections(now);
  }

  /**
   * Click tracking for Hello Retail cards (Claude 21.09).
   *
   * The cards link to `originalUrl`, which carries no `#aw_source` fragment, so
   * the Hello Retail script never sees these clicks on its own. Per the docs the
   * tracking code must then be sent with `trackClick`:
   * https://developer.helloretail.com/sdk/tracking/click_tracking/
   *
   * One delegated listener covers every card, including the loop clones (which
   * copy the attribute). It listens in the bubble phase and skips clicks whose
   * default was prevented, so a mouse drag across the carousel is not counted.
   */
  function hrBindClickTracking() {
    document.addEventListener('click', (event) => {
      if (event.defaultPrevented) return;
      const link = event.target.closest?.('a[href]');
      if (!link) return;
      const card = link.closest('[data-hr-tracking-code]');
      if (!card) return;
      const trackingCode = card.getAttribute('data-hr-tracking-code');
      if (!trackingCode) return;
      window.hrq = window.hrq || [];
      window.hrq.push(['trackClick', trackingCode]);
    });
  }

  /**
   * A block only gets a box once its tab is selected, so its carousel measures
   * itself when the theme settles on it. `carousel:settle` does not bubble, so
   * the listener has to sit on each carousel.
   */
  function bindCarouselTabs() {
    document.querySelectorAll('featured-collections-carousel').forEach((carousel) => {
      if (carousel.dataset.fcTabsBound) return;
      carousel.dataset.fcTabsBound = 'true';
      carousel.addEventListener('carousel:settle', (event) => {
        event.detail?.cell?.querySelector('fc-loop-carousel')?.refresh?.();
      });
    });
  }

  function initFeaturedCollections() {
    bindCarouselTabs();
    hrScheduleSlots();
  }

  if (!window.__mmHelloRetailFeaturedCollections) {
    window.__mmHelloRetailFeaturedCollections = true;

    // Both of these run before the DOM work below, so the network has a head
    // start on the two slowest external dependencies.
    hrTrackingUserId();

    // Bound once per page, never per section load.
    hrBindClickTracking();

    hrOnReady(initFeaturedCollections);
    document.addEventListener('shopify:section:load', (event) => {
      if (
        event.target.querySelector?.('featured-collections-carousel') ||
        event.target.querySelector?.('[data-hello-retail-key]')
      ) {
        initFeaturedCollections();
      }
    });
  }
})();